import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';

/**
 * Staff-side dialogs for the secure document link.
 *  - RequestDocumentsModal: pick what to ask the patient for; the text goes
 *    out from the thread like any staff message.
 *  - SendDocumentsModal: attach PDFs / images for the patient to view over
 *    the same verified link (statements, letters, anything that must not
 *    travel inside an SMS).
 */
export function RequestDocumentsModal({ practiceId, conversation, categories, onClose, onSent }) {
  const [picked, setPicked] = useState(() => new Set(['drivers_license', 'insurance_front', 'insurance_back']));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = (k) => setPicked(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.createDocumentRequest(practiceId, {
        patientId: conversation.patient_id, categories: [...picked], note: note.trim() || null, conversationId: conversation.id,
      });
      onSent?.(r.data);
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not send the request');
    } finally {
      setBusy(false);
    }
  };

  const chosen = categories.filter(c => picked.has(c.key)).map(c => c.label.toLowerCase());
  return (
    <Modal title={`Request documents from ${conversation.first_name}`} onClose={onClose}>
      <p className="hint">The patient gets a text with a secure link. They confirm their last name and date of birth, then upload each item from their phone. Files appear in this thread for review before anything is filed to athena.</p>
      <div className="grid2">
        {categories.map(c => (
          <label key={c.key} className={`opt${picked.has(c.key) ? ' on' : ''}`}>
            <input type="checkbox" checked={picked.has(c.key)} onChange={() => toggle(c.key)} />
            <span>{c.label}{!c.pushable && <small>view only</small>}</span>
          </label>
        ))}
      </div>
      <label className="lbl">Add a note for the patient (optional)
        <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. We need these before Thursday’s visit." />
      </label>
      <div className="preview">
        <b>Preview</b>
        <span>{chosen.length ? `Please send us ${chosen.join(', ')} using this secure link: https://…/s/xxxx ${note.trim()} You'll confirm your last name and date of birth first. The link expires in 7 days.` : 'Pick at least one document type.'}</span>
      </div>
      {err && <div className="err">{err}</div>}
      <div className="row">
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn primary" onClick={send} disabled={busy || picked.size === 0}>{busy ? 'Sending…' : 'Send request'}</button>
      </div>
    </Modal>
  );
}

export function SendDocumentsModal({ practiceId, conversation, onClose, onSent }) {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  const add = (list) => {
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (!/^(image\/|application\/pdf)/.test(f.type)) { setErr(`${f.name}: only images and PDFs`); continue; }
      if (f.size > 10 * 1024 * 1024) { setErr(`${f.name}: larger than 10 MB`); continue; }
      if (next.length >= 5) { setErr('Up to 5 files per send'); break; }
      next.push(f);
    }
    setFiles(next);
  };
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.sendPatientDocuments(practiceId, { patientId: conversation.patient_id, files, note: note.trim() || null, conversationId: conversation.id });
      onSent?.(r.data);
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Send documents to ${conversation.first_name}`} onClose={onClose}>
      <p className="hint">The files never travel inside the text. The patient gets a secure link, confirms their last name and date of birth, and opens them from there. Links stay open for 7 days.</p>
      <div className="drop" onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); add(e.dataTransfer.files); }}>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => { add(e.target.files); e.target.value = ''; }} />
        {files.length === 0 ? <span>Drop PDFs or images here, or click to choose (up to 5, 10 MB each)</span> : (
          <ul>
            {files.map((f, i) => (
              <li key={i}><span>{f.name}</span><small>{(f.size / 1024).toFixed(0)} KB</small>
                <button type="button" onClick={e => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}>×</button></li>
            ))}
          </ul>
        )}
      </div>
      <label className="lbl">Add a note for the patient (optional)
        <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Here is your statement for October." />
      </label>
      {err && <div className="err">{err}</div>}
      <div className="row">
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn primary" onClick={send} disabled={busy || files.length === 0}>{busy ? 'Sending…' : `Send ${files.length || ''} ${files.length === 1 ? 'file' : 'files'}`}</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="nh-modal-bg" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nh-modal nh-docmodal" role="dialog" aria-modal="true">
        <h4>{title}</h4>
        {children}
      </div>
    </div>
  );
}
